import { useEffect, useRef, useState } from "react";
import type { ToolDispatchResult } from "../lib/agentTypes";

export interface PendingAttachment {
  /** Project-relative path, always starts with "attachments/". */
  path: string;
  size: number;
}

export type UiBlock =
  | { type: "text"; text: string }
  | {
      type: "tool_call";
      id: string;
      name: string;
      input: Record<string, unknown>;
      result?: ToolDispatchResult;
    };

export interface UiMessage {
  // "system" = a user-only interjection (e.g. an auto-top-up notice). Rendered in the stream
  // but NEVER sent to the model.
  id: string;
  kind: "user" | "assistant" | "system";
  blocks: UiBlock[];
  complete: boolean;
  ts: number;
}

export type CutoffReason = "max_tokens" | "max_turns" | "error" | "out_of_credits";

export interface ChatProps {
  messages: UiMessage[];
  running: boolean;
  /** `attachments` is non-empty when the user attached pasted text to this submission. */
  onSend(text: string, attachments: PendingAttachment[]): void;
  onAbort?: () => void;
  disabled?: boolean;
  disabledReason?: string;
  /** When set, render a "Continue" banner above the input so the user can resume a truncated run. */
  cutoff?: { reason: CutoffReason; message?: string } | null;
  onContinue?: () => void;
  /** Called when the user submits the attach modal — writes the file and returns its path/size.
   *  `deployed=true` routes the file under `assets/<name>` so it's bundled with the deployed
   *  app and fetchable at runtime; `false` (default) routes to `attachments/<name>` for
   *  build-time-only reference material that the agent reads via read_file. */
  onAttach?(opts: { name: string; content: string; deployed: boolean }): Promise<PendingAttachment>;
  /** Clears the saved conversation. Asks for confirmation. Available regardless of viewport
   *  so a stuck-in-context-rot run can always be reset from inside the chat pane. */
  onClear?: () => void;
}

const MAX_ATTACHMENT_BYTES = 500 * 1024; // 500 KB — anything larger burns too much context

const CUTOFF_LABEL: Record<CutoffReason, string> = {
  max_tokens: "Response was cut off (token limit hit mid-response)",
  max_turns: "Hit the tool-call turn cap — the model isn't done",
  error: "Run stopped with an error",
  out_of_credits: "Out of credits — top up to continue this build",
};

export function Chat({ messages, running, onSend, onAbort, disabled, disabledReason, cutoff, onContinue, onAttach, onClear }: ChatProps) {
  const [draft, setDraft] = useState("");
  const [pending, setPending] = useState<PendingAttachment[]>([]);
  const [attachOpen, setAttachOpen] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, running]);

  function submit() {
    const t = draft.trim();
    // Allow sending with just attachments + no text (rare, but you might want "build me an
    // app from this rulebook" with the prompt entirely in the attachment).
    if ((!t && pending.length === 0) || running || disabled) return;
    setDraft("");
    const atts = pending;
    setPending([]);
    onSend(t, atts);
  }

  function removePending(path: string) {
    setPending((prev) => prev.filter((p) => p.path !== path));
  }

  return (
    <div className="pane" style={{ minWidth: 320 }}>
      <div className="pane-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span>Chat</span>
        {onClear && messages.length > 0 && !running && (
          <button
            className="btn-ghost"
            style={{ fontSize: 11, padding: "1px 8px", textTransform: "none", letterSpacing: 0 }}
            onClick={onClear}
            title="Clear the conversation history. Files and deployments are kept; only the chat resets. Useful if the agent gets stuck in a bad loop."
            aria-label="Clear conversation history"
          >
            Reset chat
          </button>
        )}
      </div>
      <div className="pane-body chat-list" ref={listRef}>
        {messages.length === 0 && (
          <div style={{ color: "var(--fg-faint)", padding: 12, fontSize: 13 }}>
            Describe the app you want — a d100 roller for the table, a buzz-in trivia round, a vibe-check poll.
            Claude will build it, validate it, screenshot it, and hand you a shareable URL.
          </div>
        )}
        {messages.map((m) =>
          m.kind === "system" ? (
            // User-only interjection (e.g. an auto-top-up notice). Subtle, centered; never a bubble.
            <div
              key={m.id}
              className="msg-system"
              style={{
                textAlign: "center",
                fontSize: 12,
                color: "var(--fg-dim)",
                margin: "6px auto",
                padding: "6px 12px",
                border: "1px solid var(--border)",
                borderRadius: 8,
                background: "var(--panel)",
                maxWidth: "90%",
              }}
            >
              {m.blocks.map((b, i) => (b.type === "text" ? <span key={i}>{b.text}</span> : null))}
            </div>
          ) : (
            <div key={m.id} className={"msg " + m.kind}>
              <div className="role">{m.kind}</div>
              {m.blocks.map((b, i) => {
                if (b.type === "text") {
                  return <div key={i}>{b.text}</div>;
                }
                return <ToolCallCard key={b.id} block={b} />;
              })}
              {m.kind === "assistant" && !m.complete && (
                <div style={{ color: "var(--fg-faint)", marginTop: 6 }}>
                  <span className="spinner" /> thinking…
                </div>
              )}
            </div>
          )
        )}
      </div>
      {cutoff && !running && (
        <div
          style={{
            borderTop: "1px solid var(--border)",
            background: "#211a07",
            color: "#e0af68",
            padding: "8px 12px",
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontSize: 12,
          }}
        >
          <span style={{ flex: 1 }}>
            {cutoff.message ?? CUTOFF_LABEL[cutoff.reason]}
          </span>
          {onContinue && (
            <button className="primary" onClick={onContinue}>
              ↻ Continue
            </button>
          )}
        </div>
      )}
      <div className="chat-input">
        {pending.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 4 }}>
            {pending.map((a) => (
              <span
                key={a.path}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  background: "#1c2230",
                  border: "1px solid #2a3140",
                  borderRadius: 6,
                  padding: "2px 6px 2px 8px",
                  fontSize: 11,
                  color: "#d8dee9",
                }}
                title={`${a.path} — ${a.size} bytes (excluded from deploy)`}
              >
                📎 {a.path.replace(/^attachments\//, "")} <span style={{ color: "#8b95a7" }}>· {formatBytes(a.size)}</span>
                <button
                  onClick={() => removePending(a.path)}
                  style={{ marginLeft: 2, background: "transparent", border: 0, color: "#8b95a7", cursor: "pointer", padding: 0, fontSize: 13, lineHeight: 1 }}
                  aria-label="remove attachment"
                  title="Remove (file stays in project files)"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}
        <textarea
          placeholder={disabled ? (disabledReason ?? "") : "Describe what you want, or ask for a change."}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              submit();
            }
          }}
          disabled={disabled}
        />
        <div className="row">
          <div className="hint">⌘/Ctrl + Enter to send</div>
          {!running && (
            <VoiceButton
              disabled={disabled}
              onTranscript={(text) => {
                setDraft((prev) => (prev ? prev.replace(/\s*$/, " ") + text : text));
              }}
            />
          )}
          {onAttach && !running && (
            <button
              className="btn-ghost"
              style={{ fontSize: 12 }}
              onClick={() => setAttachOpen(true)}
              disabled={disabled}
              title="Attach pasted text (RPG rulebook, custom prompts, word list, etc.). Lives at attachments/ and is excluded from deploys."
            >
              📎 Attach
            </button>
          )}
          {running ? (
            onAbort ? (
              <button className="danger" onClick={onAbort}>
                Stop
              </button>
            ) : (
              <button disabled>
                <span className="spinner" /> Running
              </button>
            )
          ) : (
            <button
              className="primary"
              onClick={submit}
              disabled={(!draft.trim() && pending.length === 0) || disabled}
            >
              Send
            </button>
          )}
        </div>
      </div>
      {attachOpen && onAttach && (
        <AttachModal
          existingNames={pending.map((p) => p.path.replace(/^(attachments|assets)\//, ""))}
          onCancel={() => setAttachOpen(false)}
          onAttach={async (opts) => {
            const att = await onAttach(opts);
            setPending((prev) => [...prev, att]);
            setAttachOpen(false);
          }}
        />
      )}
    </div>
  );
}

/**
 * Hold-to-talk voice input using the Web Speech API (built into Chrome/Edge/Safari, no
 * API key needed). Pressing & holding starts recognition; release inserts the transcript
 * into the chat draft. Auto-hides on browsers that don't support it (e.g. Firefox).
 *
 * The button only appears when the API is present, so we don't surface a disabled mic on
 * unsupported browsers. Production also needs HTTPS — Lightsail + Caddy handles that;
 * localhost gets a built-in exception.
 */
function VoiceButton({
  disabled,
  onTranscript,
}: {
  disabled?: boolean;
  onTranscript: (text: string) => void;
}) {
  const [listening, setListening] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const recognitionRef = useRef<unknown>(null);
  const transcriptRef = useRef("");

  // Capability probe — synchronous because we never want the button to flicker in
  // unsupported browsers. We accept both vendor-prefixed and unprefixed names.
  const SpeechRecognitionCtor =
    typeof window !== "undefined"
      ? ((window as unknown as {
          SpeechRecognition?: new () => SpeechRecognitionInstance;
          webkitSpeechRecognition?: new () => SpeechRecognitionInstance;
        }).SpeechRecognition ??
        (window as unknown as { webkitSpeechRecognition?: new () => SpeechRecognitionInstance }).webkitSpeechRecognition)
      : undefined;
  if (!SpeechRecognitionCtor) return null;

  function start() {
    if (listening || disabled) return;
    setError(null);
    try {
      const recog = new SpeechRecognitionCtor!();
      recog.continuous = true;
      recog.interimResults = false;
      recog.lang = (typeof navigator !== "undefined" && navigator.language) || "en-US";
      transcriptRef.current = "";
      recog.onresult = (event: SpeechRecognitionResultEvent) => {
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const r = event.results[i];
          if (r && r.isFinal && r[0]) transcriptRef.current += r[0].transcript + " ";
        }
      };
      recog.onerror = (e: { error?: string }) => {
        setError(e.error ?? "voice error");
        setListening(false);
      };
      recog.onend = () => {
        setListening(false);
        const text = transcriptRef.current.trim();
        if (text) onTranscript(text);
      };
      recog.start();
      recognitionRef.current = recog;
      setListening(true);
    } catch (e) {
      setError((e as Error).message);
      setListening(false);
    }
  }

  function stop() {
    if (!listening) return;
    try {
      (recognitionRef.current as { stop?: () => void } | null)?.stop?.();
    } catch {
      // ignore — onend will fire
    }
  }

  return (
    <button
      className="btn-ghost"
      style={{
        fontSize: 12,
        background: listening ? "#5c2121" : undefined,
        color: listening ? "#fff" : undefined,
        userSelect: "none",
      }}
      onPointerDown={(e) => {
        // Important: prevent the pointer-down from stealing focus from the textarea,
        // so the user's caret position is preserved when the transcript inserts.
        e.preventDefault();
        start();
      }}
      onPointerUp={stop}
      onPointerLeave={stop}
      onPointerCancel={stop}
      disabled={disabled}
      title={
        error
          ? `Voice error: ${error}`
          : listening
          ? "Recording — release to insert"
          : "Hold to talk (Web Speech API). Release to insert transcribed text."
      }
      aria-pressed={listening}
    >
      {listening ? "🎙 …" : "🎤 Hold"}
    </button>
  );
}

// Minimal local typings for the Web Speech API — TS lib.dom doesn't include them yet.
interface SpeechRecognitionInstance {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onresult: (e: SpeechRecognitionResultEvent) => void;
  onerror: (e: { error?: string }) => void;
  onend: () => void;
  start(): void;
  stop(): void;
}
interface SpeechRecognitionResultEvent {
  resultIndex: number;
  results: ArrayLike<SpeechRecognitionResult>;
}
interface SpeechRecognitionResult {
  isFinal: boolean;
  0: { transcript: string };
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n}B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)}KB`;
  return `${(n / 1024 / 1024).toFixed(2)}MB`;
}

function AttachModal({
  existingNames,
  onCancel,
  onAttach,
}: {
  existingNames: string[];
  onCancel: () => void;
  onAttach: (opts: { name: string; content: string; deployed: boolean }) => Promise<void>;
}) {
  const [name, setName] = useState("");
  const [content, setContent] = useState("");
  const [deployed, setDeployed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const taRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    // Autofocus the textarea on open
    setTimeout(() => taRef.current?.focus(), 0);
  }, []);

  const bytes = new Blob([content]).size;
  const overLimit = bytes > MAX_ATTACHMENT_BYTES;

  function submit() {
    setErr(null);
    if (!content.trim()) {
      setErr("Paste some content first.");
      return;
    }
    if (overLimit) {
      setErr(`Too large (${formatBytes(bytes)} > ${formatBytes(MAX_ATTACHMENT_BYTES)}). Split into smaller chunks or trim.`);
      return;
    }
    let finalName = name.trim();
    if (!finalName) {
      // Auto-name from the first non-empty line or a default.
      const firstLine = content.split("\n").find((l) => l.trim()) ?? "";
      finalName = firstLine.slice(0, 40).replace(/[^A-Za-z0-9._-]+/g, "-").replace(/^-+|-+$/g, "").toLowerCase() || "notes";
      if (!/\.[a-z0-9]+$/i.test(finalName)) finalName += ".txt";
    } else if (!/\.[a-z0-9]+$/i.test(finalName)) {
      finalName += ".txt";
    }
    // De-dupe vs already pending
    if (existingNames.includes(finalName)) {
      setErr(`"${finalName}" is already attached. Pick a different name.`);
      return;
    }
    setBusy(true);
    onAttach({ name: finalName, content, deployed }).catch((e: Error) => {
      setBusy(false);
      setErr(e.message);
    });
  }

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.72)",
        zIndex: 9100,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 18,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onCancel();
      }}
    >
      <div
        style={{
          background: "#11151c",
          color: "#d8dee9",
          border: "1px solid #232a36",
          borderRadius: 14,
          padding: 18,
          width: "100%",
          maxWidth: 640,
          boxShadow: "0 30px 80px rgba(0,0,0,0.6)",
        }}
      >
        <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 2 }}>Attach pasted text</div>
        <div style={{ fontSize: 11, color: "#8b95a7", marginBottom: 12 }}>
          Saved to <code>attachments/&lt;name&gt;</code> in the project. The agent can <code>read_file</code> it.
          NOT included in deploys. Don't paste copyrighted content you don't want the agent to reproduce verbatim.
        </div>
        <input
          type="text"
          placeholder="filename (optional — autogenerated if blank, e.g. rules.txt)"
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={busy}
          style={{
            width: "100%",
            background: "#0d1117",
            color: "#d8dee9",
            border: "1px solid #232a36",
            borderRadius: 8,
            padding: "8px 10px",
            fontSize: 12,
            fontFamily: "SF Mono, Menlo, Consolas, monospace",
            marginBottom: 8,
            boxSizing: "border-box",
          }}
        />
        <textarea
          ref={taRef}
          placeholder="paste content here…"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          disabled={busy}
          style={{
            width: "100%",
            background: "#0d1117",
            color: "#d8dee9",
            border: "1px solid #232a36",
            borderRadius: 8,
            padding: 10,
            fontSize: 12,
            fontFamily: "SF Mono, Menlo, Consolas, monospace",
            minHeight: 240,
            resize: "vertical",
            boxSizing: "border-box",
          }}
        />
        <label
          style={{
            display: "flex",
            gap: 8,
            marginTop: 10,
            fontSize: 12,
            color: "#d8dee9",
            cursor: "pointer",
            alignItems: "flex-start",
          }}
        >
          <input
            type="checkbox"
            checked={deployed}
            onChange={(e) => setDeployed(e.target.checked)}
            disabled={busy}
            style={{ marginTop: 2, width: "auto" }}
          />
          <span>
            <span style={{ fontWeight: 600 }}>Bundle with deployed app</span>
            <br />
            <span style={{ color: "#8b95a7", fontSize: 11 }}>
              Saves to <code>assets/&lt;name&gt;</code> so the app can <code>fetch("./assets/&lt;name&gt;")</code>
              {" "}at runtime. Use this for wordlists, trivia banks, card decks, etc. — content too
              big to embed inline. Unchecked: saves to <code>attachments/&lt;name&gt;</code>
              {" "}(reference material the agent reads at build time, not deployed).
            </span>
          </span>
        </label>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 10 }}>
          <span style={{ fontSize: 11, color: overLimit ? "#e06c75" : "#8b95a7" }}>
            {formatBytes(bytes)} / {formatBytes(MAX_ATTACHMENT_BYTES)}
          </span>
          <div style={{ display: "flex", gap: 6 }}>
            <button className="btn-ghost" onClick={onCancel} disabled={busy}>
              Cancel
            </button>
            <button className="primary" onClick={submit} disabled={busy || overLimit || !content.trim()}>
              {busy ? "Saving…" : "Attach"}
            </button>
          </div>
        </div>
        {err && (
          <div className="status-err" style={{ marginTop: 8, fontSize: 12 }}>
            {err}
          </div>
        )}
      </div>
    </div>
  );
}

function ToolCallCard({ block }: { block: Extract<UiBlock, { type: "tool_call" }> }) {
  const r = block.result;
  const inputSummary = summarizeInput(block.name, block.input);
  return (
    <div className="tool-card">
      <div>
        <span className="tool-name">{block.name}</span>
        {inputSummary && <span style={{ marginLeft: 6, color: "var(--fg-dim)" }}>{inputSummary}</span>}
      </div>
      {!r && (
        <div style={{ color: "var(--fg-faint)", marginTop: 4 }}>
          <span className="spinner" /> running…
        </div>
      )}
      {r && (
        <div style={{ marginTop: 4 }}>
          {r.display?.summary && (
            <div className={r.is_error ? "status-err" : "status-ok"}>
              {r.is_error ? "✗ " : "✓ "}
              {r.display.summary}
            </div>
          )}
          {r.display?.text && <pre>{r.display.text}</pre>}
          {r.display?.imageBase64 && (
            <img src={`data:image/png;base64,${r.display.imageBase64}`} alt="screenshot" />
          )}
          {r.display?.images && r.display.images.length > 0 && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4, marginTop: 4 }}>
              {r.display.images.map((img, i) => (
                <div key={i}>
                  <div style={{ fontSize: 10, color: "var(--fg-faint)" }}>{img.label}</div>
                  <img src={`data:image/png;base64,${img.imageBase64}`} alt={img.label} />
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function summarizeInput(name: string, input: Record<string, unknown>): string {
  if (name === "write_file" || name === "read_file" || name === "delete_file") return String(input.path ?? "");
  if (name === "edit_file") return String(input.path ?? "");
  if (name === "validate") {
    const parts = [];
    if (input.path) parts.push(String(input.path));
    if (input.script) parts.push("+ script");
    return parts.join(" ");
  }
  if (name === "screenshot") {
    const parts = [];
    const w = input.width, h = input.height;
    if (w && h) parts.push(`${w}x${h}`);
    if (input.path) parts.push(String(input.path));
    if (input.script) parts.push("+ script");
    return parts.join(" · ");
  }
  if (name === "web_search") return String(input.query ?? "");
  if (name === "list_files") return "";
  return JSON.stringify(input).slice(0, 80);
}
